import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Flex, Icon, icons } from '@tapresearch/ui/components';
import { useDisclosure } from '@tapresearch/ui/hooks';

import { useProjectWaves, useRootQuestionsList } from '../../../../api';
import { OpenFilterButton } from '../../../common/Filter';
import { FilterValue } from '../../../common/Filter/filterReducer';
import { FilterProvider } from '../../../common/FilterProvider';
import {
  useGetFilterFromLocation,
  useUpdateLocationFromFilter,
} from '../../../hook';
import { ActionButtons } from '../../dashboard/results';
import { LongitudinalFilterBadges } from './LongitudinalFilterBadges';
import { LongitudinalFilterDrawer } from './LongitudinalFilterDrawer';
import { LongitudinalQuestion } from './LongitudinalQuestion';
import { WaveTimelineSelect } from './WaveTimelineSelect';

export const WAVES_VIEW_OPTIONS = {
  all: 'all',
  last4: 'last4',
  last12: 'last12',
} as const;

export type WavesViewOption = keyof typeof WAVES_VIEW_OPTIONS;

export interface WavesViewState {
  option: WavesViewOption;
  ids: string[];
}

interface LongitudinalResultsProps {
  projectIdentifier: string;
}

interface LongitudinalFilter {
  filter: Record<string, string[]>;
}

export const LongitudinalResults = ({
  projectIdentifier,
}: LongitudinalResultsProps) => {
  const defaultFilter = useGetFilterFromLocation<LongitudinalFilter>({
    filter: {},
  });

  const updateFilter = (
    filterValues: Record<string, FilterValue | FilterValue[]>
  ) => {
    setLongitudinalFilter({
      ...longitudinalFilter,
      filter: filterValues as unknown as Record<string, string[]>,
    });
  };

  const [longitudinalFilter, setLongitudinalFilter] =
    useState<LongitudinalFilter>(defaultFilter as LongitudinalFilter);

  const { data: rootQuestionsList = [] } =
    useRootQuestionsList(projectIdentifier);

  const {
    onOpen: onOpenFilterDrawer,
    onClose: onCloseFilterDrawer,
    isOpen: isOpenFilterDrawer,
  } = useDisclosure();

  const { data: { data: projectWaves = [] } = {}, isLoading } = useProjectWaves(
    projectIdentifier,
    99999
  );

  const { language_id: languageId, country_id: countryId } = useMemo(() => {
    if (!isLoading && projectWaves) {
      return projectWaves[0].attributes;
    }
    return { language_id: undefined, country_id: undefined };
  }, [isLoading, projectWaves]);

  const [wavesView, setWavesView] = useState<WavesViewState>({
    option: WAVES_VIEW_OPTIONS.all,
    ids: [],
  });

  const currentWavesList = useMemo(
    () =>
      projectWaves.filter((wave) =>
        wavesView.ids.includes(wave.attributes.providable_id.toString())
      ),
    [wavesView, projectWaves]
  );

  const onChangeWavesView = useCallback(
    (value: WavesViewOption) => {
      const SLICE_SIZE: Record<WavesViewOption, 4 | 12 | undefined> = {
        [WAVES_VIEW_OPTIONS.all]: undefined,
        [WAVES_VIEW_OPTIONS.last4]: 4,
        [WAVES_VIEW_OPTIONS.last12]: 12,
      };
      const waveIds = projectWaves
        .slice(0, SLICE_SIZE[value])
        .map((campaign) => campaign.attributes.providable_id.toString());
      setWavesView({ ...wavesView, option: value, ids: waveIds });
    },
    [wavesView, projectWaves]
  );

  useEffect(() => {
    if (!!projectWaves?.length) {
      setWavesView((state) => ({
        ...state,
        ids: projectWaves.map((campaign) =>
          campaign.attributes.providable_id.toString()
        ),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useUpdateLocationFromFilter(longitudinalFilter);

  return (
    <FilterProvider
      defaultFilterValues={defaultFilter['filter']}
      onFilterUpdated={updateFilter}
    >
      <Box
        data-testid="longitudinal-results"
        {...{
          w: '95%',
          mx: 'auto',
        }}
      >
        <ActionButtons projectIdentifier={projectIdentifier} />

        <Flex mt={4} mb={8} justify="space-between">
          <Box>
            <Flex mb={2}>
              <OpenFilterButton
                data-testid="project-open-filters-button"
                onClick={onOpenFilterDrawer}
                omitFilters={['page', 'query', 'sort_by', 'status']}
              >
                <Icon mr="2" icon={icons.fi.Filter} /> Filter
              </OpenFilterButton>
            </Flex>
            <LongitudinalFilterBadges onTagClicked={onOpenFilterDrawer} />
          </Box>
          {!!projectWaves?.length && (
            <WaveTimelineSelect
              waves={projectWaves}
              wavesView={wavesView}
              onChangeWavesView={onChangeWavesView}
            />
          )}
        </Flex>
        {!!currentWavesList?.length &&
          rootQuestionsList.map((tapQuestion) => {
            const { identifier } = tapQuestion.attributes;
            return (
              <LongitudinalQuestion
                key={identifier}
                projectIdentifier={projectIdentifier}
                tapQuestion={tapQuestion.attributes}
                waves={currentWavesList}
              />
            );
          })}
      </Box>
      {languageId && countryId && (
        <LongitudinalFilterDrawer
          isOpen={isOpenFilterDrawer}
          onClose={onCloseFilterDrawer}
          countryId={countryId}
          languageId={languageId}
        />
      )}
    </FilterProvider>
  );
};
